import { Component, OnDestroy, OnInit, EventEmitter, Output, Input } from "@angular/core";
import { NonNullableFormBuilder, Validators } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";

import { BehaviorSubject, Subscription } from "rxjs";

import {
  UserService,
  User
} from "../../../../../../common/services/api/resources/user/user.service";
import { SnackbarService } from "../../../../shared/components/snackbar/snackbar.service";
import { DidService } from "../../../../../../common/services/api/resources/did/did.service";
import {
  NpaNxx,
  Nxx,
  TOLL_FREE_NPAS
} from "../../../../../../common/services/api/resources/npanxx/npanxx";
import { isAtLeastAccountAdminRole } from "../../../../../../common/services/api/role";

// NOTE
// Npa = Area Code
// Nxx = Rate center/local prefix

@Component({
  selector: "onsip-add-single-phone-number",
  templateUrl: "./add-single-phone-number.component.html",
  styleUrls: ["./add-single-phone-number.component.scss"],
  standalone: false
})
export class AddSinglePhoneNumberComponent implements OnInit, OnDestroy {
  @Input() npaNxxs!: Array<NpaNxx>;
  addPhoneNumber = this.formBuilder.group({
    areaCode: this.formBuilder.control("", Validators.required),
    localPrefix: this.formBuilder.control("", Validators.required),
    destination: this.formBuilder.control("", Validators.required),
    noCallerId: this.formBuilder.control(false),
    callerIdName: this.formBuilder.control(""),
    assignCallDetails: this.formBuilder.control("", Validators.required)
  });
  nxxs: Array<Nxx> = [];
  users: Array<User> = [];
  loadingStateComplete$ = new BehaviorSubject(true);
  @Output() closeDialog = new EventEmitter<void>();

  private unsubscriber = new Subscription();

  constructor(
    private formBuilder: NonNullableFormBuilder,
    private translate: TranslateService,
    private didService: DidService,
    private snackbar: SnackbarService,
    private userService: UserService
  ) {}

  ngOnInit() {
    this.unsubscriber.add(
      this.userService.orgState.subscribe(users => {
        this.users = users;
        const accountAdmins = users.filter(user => isAtLeastAccountAdminRole(user.roles));
        let assignee: string;
        if (accountAdmins.length > 0) {
          assignee = accountAdmins.sort((a, b) =>
            parseInt(a.userId) < parseInt(b.userId) ? -1 : 1
          )[0].userId;
        } else {
          assignee = users[0].userId;
        }
        this.addPhoneNumber.patchValue({
          assignCallDetails: assignee
        });
      })
    );

    this.unsubscriber.add(
      this.addPhoneNumber.controls.areaCode.valueChanges.subscribe(
        this.onAreaCodeChanges.bind(this)
      )
    );
  }

  onSubmit() {
    if (!this.addPhoneNumber.valid) return;
    const { areaCode, localPrefix, destination, noCallerId, callerIdName, assignCallDetails } =
      this.addPhoneNumber.getRawValue();
    this.loadingStateComplete$.next(false);
    this.didService
      .didRequest(areaCode, localPrefix, { UserId: assignCallDetails })
      .then(response => {
        if (response.status === "success") {
          const dids = Object.values(response.data);
          if (dids.length === 0) {
            this.snackbar.openSnackBar(
              "No numbers are currently available for your selected area code/prefix. Please try again or call OnSIP support on 1-800-801-3381",
              "error"
            );
            this.loadingStateComplete$.next(true);
            return;
          } else {
            const newDid = dids[0];
            this.didService
              .didAssign(newDid.didId, destination, {
                CallerIdNumberRequired: noCallerId,
                UserId: assignCallDetails
              })
              .then(response => {
                if (response.status === "success") {
                  this.didService.didEditName(newDid.didId, callerIdName).then(response => {
                    if (response.status === "success") {
                      this.snackbar.openSnackBar(
                        this.translate.instant("ONSIP_I18N.PHONE_NUMBER_HAS_BEEN_ADDED"),
                        "success"
                      );
                      this.didService.didBrowse();
                      this.closeAddNumberForm();
                    } else {
                      this.snackbar.openSnackBar(response.data.message, "error");
                    }
                  });
                } else {
                  this.snackbar.openSnackBar(response.data.message, "error");
                }
              });
          }
        } else {
          const error = response.data;
          this.snackbar.openSnackBar(
            error.code === "UserRequestDid.InsufficientFunds" ||
              error.code === "UserRequestDid.NoPrepaidCredit"
              ? error.message
              : this.translate.instant("ONSIP_I18N.THERE_WAS_AN_ERROR_SAVING_YOUR_INFORMATION"),
            "error"
          );
          this.didService.clearErrors();
        }
        this.loadingStateComplete$.next(true);
      });
  }

  getNpaDescription(npaNxx: NpaNxx): string {
    if (TOLL_FREE_NPAS.includes(npaNxx.num)) return this.translate.instant("ONSIP_I18N.TOLL_FREE");
    return npaNxx?.postalCode?.name || this.translate.instant("ONSIP_I18N.UNKNOWN");
  }

  closeAddNumberForm(): void {
    this.closeDialog.emit();
  }

  ngOnDestroy() {
    this.unsubscriber.unsubscribe();
  }

  private onAreaCodeChanges(npaNum: string) {
    const foundNpaNxx = this.npaNxxs.find(npaNxx => npaNxx.num === npaNum);
    this.nxxs = foundNpaNxx
      ? foundNpaNxx.nxxs.sort((a, b) => (a.rateCenter + a.num < b.rateCenter + b.num ? -1 : 1))
      : [];
    this.addPhoneNumber.controls.localPrefix.setValue("");
  }
}
